import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Pill, Apple, ShieldCheck, Users, MapPin, Phone, ClipboardList, CalendarClock, AlertTriangle, HeartHandshake } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Button, Badge, PullQuote } from '../../components/portal/ui'
import { HumanArt } from '../../components/portal/HumanArt'
import { usePublishedTestimonials, testimonialAttribution } from '../../components/portal/Voices'

// Section anchor IDs are kept in code (not derived from translated titles) so that
// in-page nav links from HomePage/audiences (#treatment, #nutrition, #rights, …) keep
// working in every language. Only the title/body text is localized (pulled by index).
const SECTION_META = [
  { id: 'what-to-do', icon: ClipboardList },
  { id: 'treatment', icon: Pill },
  { id: 'adherence', icon: CalendarClock },
  { id: 'nutrition', icon: Apple },
  { id: 'side-effects', icon: AlertTriangle },
  { id: 'family', icon: Users },
  { id: 'rights', icon: ShieldCheck },
  { id: 'where-to-get-help', icon: MapPin },
]

export default function IHaveTBPage() {
  const { t } = useTranslation()
  // A real survivor/patient voice leads the page — never fabricated.
  const { voices } = usePublishedTestimonials(3)
  const survivor = voices.find((v) => v.authorRole === 'SURVIVOR' || v.authorRole === 'PATIENT') ?? voices[0]

  const quickActions = t('pages.iHaveTb.quickActions', { returnObjects: true }) as string[]
  const journeySteps = t('pages.iHaveTb.journey.steps', { returnObjects: true }) as { title: string; desc: string }[]
  const sections = t('pages.iHaveTb.sections', { returnObjects: true }) as { title: string; body: string }[]

  return (
    <PortalLayout>
      <Seo
        title={t('pages.iHaveTb.seo.title')}
        description={t('pages.iHaveTb.seo.description')}
        canonicalPath="/i-have-tb"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.iHaveTb', 'I Have TB'), path: '/i-have-tb' }]}
      />
      {/* HERO — Human story first, then reassurance, then action */}
      <Section className="pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.iHaveTb', 'I Have TB') }]} />
          <div className="grid lg:grid-cols-2 gap-8 items-center mt-2">
            <div className="order-2 lg:order-1">
              <div className="rounded-3xl bg-warm-100/60 border border-warm-200 p-6">
                <HumanArt variant="walking-together" className="w-full h-auto max-w-sm mx-auto" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              {survivor ? (
                (() => {
                  const { author, role } = testimonialAttribution(survivor)
                  return <PullQuote quote={survivor.body} author={author} role={role} to="/stories" linkLabel={t('common.readStory')} className="mb-6" />
                })()
              ) : null}
              <SectionHeading as="h1" eyebrow={t('pages.iHaveTb.hero.eyebrow')} title={t('pages.iHaveTb.hero.title')} description={t('pages.iHaveTb.hero.description')} />
              {/* Reassurance, demoted from an institutional Alert to a plain, warm line */}
              <p className="text-snow-700 text-pretty">
                {t('pages.iHaveTb.hero.reassurance')}
              </p>
            </div>
          </div>
        </Container>
      </Section>

      {/* Quick action bar */}
      <Section className="bg-white pt-4 pb-8">
        <Container>
          <div className="flex flex-wrap gap-3">
            <Button to="/get-help" variant="primary" icon={<MapPin className="w-4 h-4" />}>{quickActions[0]}</Button>
            <a href="tel:1800116666" className="btn-secondary flex items-center gap-2"><Phone className="w-4 h-4" aria-hidden="true" /> {quickActions[1]}</a>
            <Button to="/support-a-patient" variant="outline" icon={<HeartHandshake className="w-4 h-4" />}>{quickActions[2]}</Button>
          </div>
        </Container>
      </Section>

      {/* Treatment journey steps */}
      <Section id="treatment" className="bg-snow-50">
        <Container>
          <SectionHeading eyebrow={t('pages.iHaveTb.journey.eyebrow')} title={t('pages.iHaveTb.journey.title')} />
          <ol className="space-y-4">
            {journeySteps.map((s, i) => (
              <li key={i}>
                <Card className="p-5 flex gap-4 items-start">
                  <span className="shrink-0 w-10 h-10 rounded-full bg-[rgb(15_76_129)] text-white font-bold flex items-center justify-center" aria-hidden="true">{i + 1}</span>
                  <div>
                    <h3 className="font-semibold text-snow-900">{s.title}</h3>
                    <p className="text-sm text-snow-600 mt-1">{s.desc}</p>
                  </div>
                </Card>
              </li>
            ))}
          </ol>
        </Container>
      </Section>

      {/* Detailed sections */}
      <Section className="bg-white">
        <Container>
          <div className="space-y-6">
            {sections.map((s, i) => {
              const meta = SECTION_META[i] ?? SECTION_META[0]
              const Icon = meta.icon
              return (
                <Card key={meta.id} id={meta.id} className="p-6 scroll-mt-24">
                  <div className="flex items-start gap-4">
                    <span className="shrink-0 w-11 h-11 rounded-xl bg-[rgb(232_241_252)] text-[rgb(15_76_129)] flex items-center justify-center" aria-hidden="true">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div>
                      <h2 className="text-lg font-bold text-snow-900">{s.title}</h2>
                      <p className="text-snow-600 mt-2 text-pretty">{s.body}</p>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        </Container>
      </Section>

      {/* Nutrition detail */}
      <Section id="nutrition" className="bg-snow-50">
        <Container>
          <Card className="p-8 bg-gradient-to-br from-[rgb(232_252_243)] to-white">
            <Badge tone="green" className="mb-3"><Apple className="w-3.5 h-3.5" aria-hidden="true" /> {t('pages.iHaveTb.nutrition.badge')}</Badge>
            <h2 className="text-xl font-bold text-snow-900">{t('pages.iHaveTb.nutrition.title')}</h2>
            <p className="text-snow-600 mt-2 max-w-2xl">
              {t('pages.iHaveTb.nutrition.body')}
            </p>
            <div className="mt-4">
              <Button to="/resources?type=infographic" variant="secondary" size="sm">{t('pages.iHaveTb.nutrition.cta')}</Button>
            </div>
          </Card>
        </Container>
      </Section>

      {/* Next steps */}
      <Section className="bg-white pt-0">
        <Container>
          <Card className="p-8 text-center">
            <h2 className="text-xl font-bold text-snow-900">{t('pages.iHaveTb.nextSteps.title')}</h2>
            <p className="text-snow-600 mt-2 max-w-xl mx-auto">{t('pages.iHaveTb.nextSteps.body')}</p>
            <div className="flex flex-wrap gap-3 justify-center mt-5">
              <Button to="/get-help" variant="primary" icon={<MapPin className="w-4 h-4" />}>{t('pages.iHaveTb.nextSteps.ctaGetHelp')}</Button>
              <Button to="/support-a-patient" variant="outline">{t('pages.iHaveTb.nextSteps.ctaCommunity')}</Button>
              <Link to="/about-tb" className="btn-ghost">{t('pages.iHaveTb.nextSteps.ctaLearn')}</Link>
            </div>
          </Card>
        </Container>
      </Section>
    </PortalLayout>
  )
}